import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, DollarSign, Martini, Percent, CheckCircle2, Camera, Star } from 'lucide-react';
import StarRating from './StarRating';
import WantToTryButton from './WantToTryButton';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { getBarLocationLabel } from '../utils/barLocation';

function BarCard({ bar, showIndicators = false, isDive: propIsDive, isRated: propIsRated, myRating: propMyRating }) {
  const { getBarStats, getBarThumbnail, ratings } = useApp();
  const { user } = useAuth();
  const stats = getBarStats(bar.id);
  const thumbnail = getBarThumbnail(bar.id);
  
  // Use props if provided, otherwise calculate
  const isDiveBar = propIsDive !== undefined ? propIsDive : stats.divePercentage > 50;
  const isRatedByUser = propIsRated !== undefined 
    ? propIsRated 
    : (user ? ratings.some(r => r.barId === bar.id && r.userId === user.id) : false);
  const myRating = propMyRating;

  const attributes = [];
  if (bar.attributes?.poolTable) attributes.push('Pool');
  if (bar.attributes?.darts) attributes.push('Darts');
  if (bar.attributes?.cashOnly) attributes.push('Cash Only');
  if (bar.attributes?.jukebox) attributes.push('Jukebox');
  if (bar.attributes?.outdoorSeating) attributes.push('Patio');
  if (bar.attributes?.liveMusic) attributes.push('Live Music');
  if (bar.attributes?.happyHour) attributes.push('Happy Hour');

  return (
    <div className="bar-card bg-dive-dark border border-dive-gray rounded-xl overflow-hidden hover:border-dive-neon/50 transition-colors">
      {/* Photo thumbnail or placeholder */}
      <Link to={`/bar/${bar.id}`} className="block">
        <div className="relative h-32 bg-dive-darker">
          {thumbnail ? (
            <img
              src={thumbnail.url}
              alt={bar.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera className="w-8 h-8 text-gray-700" />
            </div>
          )}
          
          {/* Badges in top left */}
          <div className="absolute top-2 left-2 flex gap-1">
            {isDiveBar && (
              <div className="flex items-center gap-1 bg-dive-neon/90 text-white px-2 py-1 rounded-full text-xs">
                <CheckCircle2 className="w-3 h-3" />
                Dive Bar
              </div>
            )}
            {isRatedByUser && (
              <div className="flex items-center gap-1 bg-amber-500/90 text-white px-2 py-1 rounded-full text-xs">
                <Star className="w-3 h-3 fill-white" />
                {myRating ? myRating.toFixed(1) : 'Rated'}
              </div>
            )}
          </div>
        </div>
      </Link>

      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <Link to={`/bar/${bar.id}`} className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-display text-xl text-white tracking-wide hover:text-dive-neon transition-colors">
                {bar.name}
              </h3>
              {/* Small indicator dots */}
              {showIndicators && (
                <div className="flex gap-1">
                  {isDiveBar && (
                    <div 
                      className="w-2 h-2 rounded-full bg-dive-neon" 
                      title="Community Rated Dive Bar"
                    />
                  )}
                  {isRatedByUser && (
                    <div 
                      className="w-2 h-2 rounded-full bg-amber-500" 
                      title="You've rated this bar"
                    />
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 text-gray-400 text-sm">
              <MapPin className="w-3 h-3" />
              <span>{getBarLocationLabel(bar)}</span>
            </div>
          </Link>
          
          <div className="flex items-center gap-2">
            {user && (
              <WantToTryButton barId={bar.id} size="small" showLabel={false} />
            )}
            <div className="text-right">
              <div className="flex items-center gap-2 mb-1">
                <StarRating rating={stats.overall} size="sm" />
                <span className="text-dive-amber font-bold">
                  {stats.overall > 0 ? stats.overall.toFixed(1) : '—'}
                </span>
              </div>
              <div className="text-xs text-gray-500 whitespace-nowrap">
                {stats.totalRatings === 0 ? (
                  <span className="text-dive-amber">Be first to rate!</span>
                ) : (
                  <>{stats.totalRatings} {stats.totalRatings === 1 ? 'rating' : 'ratings'}</>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sub-ratings */}
        <div className="grid grid-cols-4 gap-2 mb-3 text-xs">
          <div className="flex flex-col items-center p-2 bg-dive-darker rounded-lg">
            <DollarSign className="w-4 h-4 text-dive-green mb-1" />
            <span className="text-white font-medium">
              {stats.price > 0 ? stats.price.toFixed(1) : '—'}
            </span>
            <span className="text-gray-500">Price</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-dive-darker rounded-lg">
            <Martini className="w-4 h-4 text-dive-blue mb-1" />
            <span className="text-white font-medium">
              {stats.ambiance > 0 ? stats.ambiance.toFixed(1) : '—'}
            </span>
            <span className="text-gray-500">Vibe</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-dive-darker rounded-lg">
            <Users className="w-4 h-4 text-dive-amber mb-1" />
            <span className="text-white font-medium">
              {stats.crowd > 0 ? stats.crowd.toFixed(1) : '—'}
            </span>
            <span className="text-gray-500">Crowd</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-dive-darker rounded-lg">
            <Percent className="w-4 h-4 text-dive-neon mb-1" />
            <span className="text-white font-medium">
              {stats.divePercentage > 0 ? `${Math.round(stats.divePercentage)}%` : '—'}
            </span>
            <span className="text-gray-500">Dive</span>
          </div>
        </div>

        {/* Attributes */}
        {attributes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attributes.slice(0, 4).map(attr => (
              <span
                key={attr}
                className="px-2 py-0.5 bg-dive-gray/50 text-gray-300 text-xs rounded-full"
              >
                {attr}
              </span>
            ))}
            {attributes.length > 4 && (
              <span className="px-2 py-0.5 text-gray-500 text-xs">
                +{attributes.length - 4} more
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BarCard;
